/**
 * /signup/confirm?token=… — підтвердження пошти з листа (ч.1122).
 *
 * Обліковий запис створюється ТУТ, після натискання кнопки, а не під час
 * заповнення форми реєстрації. Кнопка навмисна: поштові фільтри відкривають
 * посилання з листів автоматично — саме відкриття сторінки нічого не створює.
 * Посилання одноразове; нове скасовує попереднє.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { checkSignup, confirmSignup } from '@/api/auth';
import { useAuth } from '@/auth/store';
import { extractApiError } from '@/utils/extractApiError';

export default function SignupConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const check = useQuery({
    queryKey: ['signup-check', token],
    queryFn: () => checkSignup(token!),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  async function onConfirm() {
    setError(null);
    setLoading(true);
    try {
      const tok = await confirmSignup(token!);
      setSession(tok.user_id ?? null);
      // Застарілий активний акаунт попередньої сесії цього браузера прибираємо:
      // інакше перший запит піде з чужим X-Tenant-Id і отримає 403.
      setActiveTenant(null);
      // ч.1072: новий користувач потрапляє в «Обліковий запис» (пароль і
      // двофакторний вхід); організацію додає кнопкою в шапці.
      navigate('/select-tenant', { replace: true, state: { next: '/account' } });
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        setInvalid(true);
      } else if (status === 409) {
        setError('Цю пошту вже зареєстровано — увійдіть або відновіть пароль.');
      } else {
        setError(extractApiError(e, 'Не вдалося завершити реєстрацію. Спробуйте ще раз.'));
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token || check.isError || check.data?.valid === false || invalid) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Посилання недійсне</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Посилання застаріло, уже використане або замість нього надіслано
            нове. Якщо реєстрацію вже підтверджено — просто увійдіть. Якщо
            ні — зареєструйтесь ще раз, і ми надішлемо новий лист.
          </p>
          <Link to="/login">
            <button type="button" className="primary" style={{ width: '100%' }}>увійти</button>
          </Link>
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
            <Link to="/signup">Зареєструватися ще раз</Link>
          </p>
        </form>
      </div>
    );
  }

  if (check.isLoading || !check.data) {
    return (
      <div className="login">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Підтвердження реєстрації</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>перевірка посилання…</p>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form onSubmit={(e) => e.preventDefault()}>
        <h1>Підтвердження реєстрації</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Пошту <strong>{check.data.email}</strong> підтверджено посиланням з
          листа. Натисніть кнопку — ми створимо обліковий запис і виконаємо вхід.
        </p>
        <button type="button" className="primary" style={{ width: '100%' }}
          onClick={onConfirm} disabled={loading}>
          {loading ? 'створюємо обліковий запис…' : 'підтвердити і увійти'}
        </button>
        {error && <div className="error">{error}</div>}
        <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center' }}>
          <Link to="/login">Повернутися до входу</Link>
        </p>
      </form>
    </div>
  );
}
